import { useState, type FormEvent } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { changePasswordSchema } from "@laundrypro/shared";
import { Button, ErrorText, Input } from "../components/ui";
import { LaundryMark } from "../components/LaundryMark";
import { useAuth } from "../context/AuthContext";
import { useBranding } from "../context/BrandingContext";
import { ApiError, apiFetch, setCsrfToken } from "../lib/api";

/**
 * Password change, and the forced screen for an account whose password was set
 * by an administrator.
 *
 * The server confines such an account to this route; this page is the way out of
 * that state, so it must not itself require anything the account cannot reach.
 */
export function ChangePasswordPage() {
  const { language } = useBranding();
  const { user, loading, refresh, mustChangePassword } = useAuth();
  const navigate = useNavigate();
  const ar = language === "ar";

  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);

  if (!loading && !user && !mustChangePassword) return <Navigate to="/login" replace />;

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    setFieldErrors({});

    if (newPassword !== confirm) {
      setFieldErrors({ confirm: ar ? "كلمتا المرور غير متطابقتين." : "Passwords do not match." });
      return;
    }

    const parsed = changePasswordSchema.safeParse({ currentPassword, newPassword });
    if (!parsed.success) {
      const errors: Record<string, string> = {};
      for (const issue of parsed.error.issues) errors[issue.path.join(".")] ??= issue.message;
      setFieldErrors(errors);
      return;
    }

    setSubmitting(true);
    try {
      // Changing a password revokes every other session and issues a fresh one,
      // so the CSRF token must be replaced with the one returned here.
      const result = await apiFetch<{ csrfToken: string }>("/auth/change-password", {
        method: "POST",
        body: parsed.data,
      });
      setCsrfToken(result.csrfToken);
      await refresh();
      navigate("/dashboard", { replace: true });
    } catch (err) {
      if (err instanceof ApiError) {
        setError(
          err.code === "invalid_current_password"
            ? ar
              ? "كلمة المرور الحالية غير صحيحة."
              : "Your current password is incorrect."
            : err.message,
        );
        if (err.fields) setFieldErrors(err.fields);
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex min-h-dvh items-center justify-center bg-ink-950 px-4 py-10">
      <div className="w-full max-w-sm animate-slide-up rounded-2xl border border-white/10 bg-card p-6 shadow-lg sm:p-8">
        <div className="mb-6 text-center">
          <LaundryMark size={40} className="mx-auto mb-3" />
          <h1 className="text-lg font-semibold text-ink-900">
            {ar ? "تغيير كلمة المرور" : "Change your password"}
          </h1>
        </div>

        {mustChangePassword && (
          <p className="mb-4 rounded-lg border border-warning-border bg-warning-bg px-3 py-2 text-xs text-warning">
            {ar
              ? "يجب تعيين كلمة مرور جديدة قبل متابعة استخدام النظام."
              : "You must set a new password before continuing."}
          </p>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <Input
            label={ar ? "كلمة المرور الحالية" : "Current password"}
            type="password"
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            error={fieldErrors.currentPassword}
            autoComplete="current-password"
            required
          />
          <Input
            label={ar ? "كلمة المرور الجديدة" : "New password"}
            type="password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            error={fieldErrors.newPassword}
            helperText={
              ar
                ? "١٠ أحرف على الأقل، وتحتوي على حرف ورقم."
                : "At least 10 characters, including a letter and a number."
            }
            autoComplete="new-password"
            required
          />
          <Input
            label={ar ? "تأكيد كلمة المرور" : "Confirm new password"}
            type="password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            error={fieldErrors.confirm}
            autoComplete="new-password"
            required
          />

          <ErrorText>{error}</ErrorText>

          <Button type="submit" size="lg" className="w-full" loading={submitting}>
            {ar ? "حفظ" : "Change password"}
          </Button>
        </form>
      </div>
    </div>
  );
}
